import { api } from './api'
import { getAll } from './pagination'

export interface ExamType {
  id: number
  name: string
  code: string
  description: string
  duration_minutes: number
  total_marks: number
  is_mandatory: boolean
  is_major_exam: boolean
  passing_marks: number
  weight_percentage: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface Exam {
  id: number
  name: string
  description: string
  exam_type: number
  subject: number
  academic_year: number
  academic_year_name: string
  exam_type_name: string
  is_major_exam: boolean
  subject_name: string
  exam_date: string
  start_time: string
  end_time: string
  total_marks: number
  passing_marks: number
  duration_minutes: number
  venue?: string
  /** The AcademicTerm's id, not its name; look the name up in the calendar. */
  term?: number | null
  term_name: string
  room_capacity?: number | null
  instructions?: string | null
  materials_allowed?: string | null
  status: 'draft' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'postponed'
  is_published: boolean
  created_by: number
  notes: string
  classes: number[]
  class_names: string[]
  results_status: 'draft' | 'submitted' | 'moderated' | 'approved' | 'published'
  created_by_name: string
  moderated_by_name?: string
  moderated_at?: string | null
  approved_by_name?: string
  approved_at?: string | null
  returned_reason: string
  returned_by_name: string
  returned_at?: string | null
  candidate_count: number
  marked_count: number
  absent_count: number
  passed_count: number
  average_percentage: number | null
  /** The signed-in teacher teaches this subject to a class sitting it, so may mark it. */
  can_mark: boolean
  document?: string | null
  document_uploaded_by_name?: string | null
  document_uploaded_at?: string | null
  created_at: string
  updated_at: string
}

export interface ExamSession {
  id: number
  exam: number
  exam_name: string
  student: number
  student_name: string
  student_number: string
  seat_number?: string
  hall?: string
  status: 'scheduled' | 'in_progress' | 'completed' | 'absent' | 'cancelled'
  is_present: boolean
  check_in_time?: string
  submission_time?: string
  notes: string
  class_name: string
  invigilator_name?: string
  result: { id: number; marks_obtained: string | null; percentage: string | null; grade: string | null; is_passed: boolean; is_absent: boolean; remarks: string } | null
  created_at: string
  updated_at: string
}

export interface ExamResult {
  id: number
  exam_session: number
  exam_session_name: string
  total_marks: number
  marks_obtained?: number
  percentage?: number
  grade?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'N/A'
  is_passed: boolean
  remarks: string
  marked_by?: number
  marked_date?: string
  created_at: string
  updated_at: string
}

export interface ExaminationStats {
  total_exams: number
  active_sessions: number
  published_exams: number
  results_recorded: number
  average_score: number
  pass_rate: number
}

export interface ExamSchedule {
  id: number
  name: string
  description: string
  start_date: string
  end_date: string
  academic_year: number
  academic_year_name: string
  term: number | null
  term_name: string
  is_active: boolean
  is_published: boolean
  created_by_name: string
}

export const examinationService = {
  getExamSchedules: async (): Promise<ExamSchedule[]> =>
    getAll('/examination/exam-schedules/'),
  createExamSchedule: async (data: Partial<ExamSchedule>): Promise<ExamSchedule> =>
    (await api.post('/examination/exam-schedules/', data)).data,
  automateExamSchedule: async (id: number) =>
    (await api.post(`/examination/exam-schedules/${id}/automate/`)).data,
  generateCandidates: async (id: number, data: { class_ids: number[]; invigilator_ids: number[]; venue: string; room_capacity: number }) =>
    (await api.post(`/examination/exam-schedules/${id}/generate_candidates/`, data)).data,
  publishExamSchedule: async (id: number): Promise<ExamSchedule> =>
    (await api.post(`/examination/exam-schedules/${id}/publish/`)).data,
  // Exam Types
  getExamTypes: async (): Promise<ExamType[]> => {
    return getAll('/examination/exam-types/')
  },

  getExamType: async (id: number): Promise<ExamType> => {
    const response = await api.get(`/examination/exam-types/${id}/`)
    return response.data
  },

  createExamType: async (examType: Partial<ExamType>): Promise<ExamType> => {
    const response = await api.post('/examination/exam-types/', examType)
    return response.data
  },

  updateExamType: async (id: number, examType: Partial<ExamType>): Promise<ExamType> => {
    const response = await api.patch(`/examination/exam-types/${id}/`, examType)
    return response.data
  },

  deleteExamType: async (id: number): Promise<void> => {
    await api.delete(`/examination/exam-types/${id}/`)
  },

  // Exams
  getExams: async (): Promise<Exam[]> => {
    return getAll('/examination/exams/')
  },

  getExam: async (id: number): Promise<Exam> => {
    const response = await api.get(`/examination/exams/${id}/`)
    return response.data
  },

  createExam: async (exam: Partial<Exam>, documentFile?: File | null): Promise<Exam> => {
    if (!documentFile) {
      const response = await api.post('/examination/exams/', exam)
      return response.data
    }
    const formData = new FormData()
    Object.entries(exam).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      if (Array.isArray(value)) value.forEach(item => formData.append(key, String(item)))
      else formData.append(key, String(value))
    })
    formData.append('document', documentFile)
    const response = await api.post('/examination/exams/', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    return response.data
  },

  updateExam: async (id: number, exam: Partial<Exam>, documentFile?: File | null): Promise<Exam> => {
    if (!documentFile) return (await api.patch(`/examination/exams/${id}/`, exam)).data
    const formData = new FormData()
    Object.entries(exam).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      if (Array.isArray(value)) value.forEach(item => formData.append(key, String(item)))
      else formData.append(key, String(value))
    })
    formData.append('document', documentFile)
    return (await api.patch(`/examination/exams/${id}/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })).data
  },

  deleteExam: async (id: number): Promise<void> => {
    await api.delete(`/examination/exams/${id}/`)
  },

  publishExam: async (id: number): Promise<Exam> => {
    const response = await api.post(`/examination/exams/${id}/publish/`)
    return response.data
  },
  bulkMarks: async (examId: number, results: Array<{ session_id: number; marks?: number; absent?: boolean; remarks?: string }>) =>
    (await api.post(`/examination/exams/${examId}/bulk_marks/`, { results })).data,
  submitResults: async (id: number): Promise<Exam> => (await api.post(`/examination/exams/${id}/submit_results/`)).data,
  moderateResults: async (id: number): Promise<Exam> => (await api.post(`/examination/exams/${id}/moderate_results/`)).data,
  approveResults: async (id: number): Promise<Exam> => (await api.post(`/examination/exams/${id}/approve_results/`)).data,
  returnResults: async ({ id, reason }: { id: number; reason: string }): Promise<Exam> => (await api.post(`/examination/exams/${id}/return_results/`, { reason })).data,
  /** One paper's candidates with their marks, for its mark sheet. */
  getPaperCandidates: async (examId: number): Promise<ExamSession[]> => getAll('/examination/exam-sessions/', { exam: examId }),
  getInvigilators: async () => (await api.get('/academics/dos/teachers/available/')).data as Array<{ id: number; first_name: string; last_name: string; user_type: string; username?: string }>,

  // Exam Sessions
  getExamSessions: async (): Promise<ExamSession[]> => {
    return getAll('/examination/exam-sessions/')
  },

  getExamSession: async (id: number): Promise<ExamSession> => {
    const response = await api.get(`/examination/exam-sessions/${id}/`)
    return response.data
  },

  createExamSession: async (session: Partial<ExamSession>): Promise<ExamSession> => {
    const response = await api.post('/examination/exam-sessions/', session)
    return response.data
  },

  updateExamSession: async (id: number, session: Partial<ExamSession>): Promise<ExamSession> => {
    const response = await api.put(`/examination/exam-sessions/${id}/`, session)
    return response.data
  },

  deleteExamSession: async (id: number): Promise<void> => {
    await api.delete(`/examination/exam-sessions/${id}/`)
  },

  // Exam Results
  getExamResults: async (): Promise<ExamResult[]> => {
    return getAll('/examination/exam-results/')
  },

  getExamResult: async (id: number): Promise<ExamResult> => {
    const response = await api.get(`/examination/exam-results/${id}/`)
    return response.data
  },

  createExamResult: async (result: Partial<ExamResult>): Promise<ExamResult> => {
    const response = await api.post('/examination/exam-results/', result)
    return response.data
  },

  updateExamResult: async (id: number, result: Partial<ExamResult>): Promise<ExamResult> => {
    const response = await api.put(`/examination/exam-results/${id}/`, result)
    return response.data
  },

  deleteExamResult: async (id: number): Promise<void> => {
    await api.delete(`/examination/exam-results/${id}/`)
  },

  // Statistics
  getExaminationStats: async (): Promise<ExaminationStats> => {
    const response = await api.get('/examination/exam-statistics/overview/')
    return response.data
  },
}
